import {
  createAsyncThunk,
  createSlice as _createSlice,
  createSelector,
  PayloadAction,
  ActionReducerMapBuilder,
} from '@reduxjs/toolkit'
import { AppRootState, Status } from 'src/store/store'
import {
  LinkedService,
  QuizV2,
  QuestionData,
  Reference,
  Section,
  StreamStep,
  Template,
  TextContent,
  TitleScreen,
} from 'src/v3/core/types'
import {
  getCourseByID,
  getCourses,
  getReferences,
  getCategories,
  getTemplates,
  getLinkedServices,
  getVideoLibrary,
  getShowNotesLibrary,
  getCourseStepsByCourseID,
  getQuizLibrary,
  getSelectedQuiz,
  getQuestionsByQuizID,
  fetchAccreditations,
  fetchCourseInstructors,
  getSeries,
  getSeriesByID,
} from './utils/api'
import {
  CourseV5Details,
  CourseAccreditationResponse,
  InstructorWithCourses,
  LightweightCourseV5,
  VideoLibraryItem,
  ShowNotesLibraryItem,
  QuizLibraryItem,
} from './course/types'
import { SeriesResponse } from './series/types'

export let fetchCategories = createAsyncThunk(
  'createV5/fetchCategories',
  getCategories,
)

export let fetchCourses = createAsyncThunk('createV5/fetchCourses', getCourses)

export let fetchLinkedServices = createAsyncThunk(
  'createV5/fetchLinkedServices',
  getLinkedServices,
)

export let fetchSelectedCourse = createAsyncThunk(
  'createV5/fetchSelectedCourse',
  (id: string) => getCourseByID(id),
)

export let fetchReferences = createAsyncThunk(
  'createV5/fetchReferences',
  getReferences,
)

export let fetchTemplates = createAsyncThunk(
  'createV5/fetchTemplates',
  getTemplates,
)

export let fetchVideoLibrary = createAsyncThunk(
  'createV5/fetchVideoLibrary',
  getVideoLibrary,
)

export let fetchShowNotesLibrary = createAsyncThunk(
  'createV5/fetchShowNotesLibrary',
  getShowNotesLibrary,
)

export let fetchCourseStepsByCourseID = createAsyncThunk(
  'createV5/fetchCourseStepsByCourseID',
  (courseID: string) => getCourseStepsByCourseID(courseID),
)

export let fetchQuizLibrary = createAsyncThunk(
  'createV5/fetchQuizLibrary',
  () => getQuizLibrary(),
)

export let fetchSelectedQuiz = createAsyncThunk(
  'createV5/fetchSelectedQuiz',
  (quizID: string) => getSelectedQuiz(quizID),
)

export let fetchQuestionsByQuizID = createAsyncThunk(
  'createV5/fetchQuestionsByQuizID',
  (quizID: string) => getQuestionsByQuizID(quizID),
)

export let fetchAllAccreditations = createAsyncThunk(
  'createV5/fetchAllAccreditations',
  fetchAccreditations,
)

export let fetchAllInstructors = createAsyncThunk(
  'createV5/fetchAllInstructors',
  fetchCourseInstructors,
)

export let fetchSeries = createAsyncThunk('createV5/fetchSeries', getSeries)

export let fetchSelectedSeries = createAsyncThunk(
  'createV5/fetchSelectedSeries',
  (id: string) => getSeriesByID(id),
)

type CreateV5State = {
  categories: Record<string, Section[]>
  categoriesStatus: Status
  courseSteps: StreamStep[]
  courseStepsStatus: Status
  courses: LightweightCourseV5[]
  coursesStatus: Status
  linkedServices: LinkedService[]
  linkedServicesStatus: Status
  selectedCourse: CourseV5Details | null
  selectedCourseLegacyCourseSteps: StreamStep[]
  selectedCourseLegacyTextContent: TextContent[]
  selectedCourseLegacyTitleScreens: TitleScreen[]
  selectedCourseStatus: Status
  selectedLinkedService: string
  references: Record<string, Reference[]>
  referencesStatus: Status
  templates: Record<string, Template[]>
  templatesStatus: Status
  videoLibrary: VideoLibraryItem[]
  videoLibraryStatus: Status
  showNotesLibrary: ShowNotesLibraryItem[]
  showNotesLibraryStatus: Status
  quizLibrary: QuizLibraryItem[]
  quizLibraryStatus: Status
  selectedQuiz: QuizV2 | null
  selectedQuizStatus: Status
  selectedQuizQuestions: QuestionData[]
  selectedQuizQuestionsStatus: Status
  accreditations: CourseAccreditationResponse[]
  accreditationsStatus: Status
  instructors: InstructorWithCourses[]
  instructorsStatus: Status
  series: SeriesResponse[]
  seriesStatus: Status
  selectedSeries: SeriesResponse | null
  selectedSeriesStatus: Status
}

export let initialState: CreateV5State = {
  categories: {},
  categoriesStatus: 'uninitialized',
  courseSteps: [],
  courseStepsStatus: 'uninitialized',
  courses: [],
  coursesStatus: 'uninitialized',
  linkedServices: [],
  linkedServicesStatus: 'uninitialized',
  references: {},
  referencesStatus: 'uninitialized',
  selectedCourse: null,
  selectedCourseLegacyCourseSteps: [],
  selectedCourseLegacyTextContent: [],
  selectedCourseLegacyTitleScreens: [],
  selectedCourseStatus: 'uninitialized',
  selectedLinkedService: 'unselected',
  templates: {},
  templatesStatus: 'uninitialized',
  videoLibrary: [],
  videoLibraryStatus: 'uninitialized',
  showNotesLibrary: [],
  showNotesLibraryStatus: 'uninitialized',
  quizLibrary: [],
  quizLibraryStatus: 'uninitialized',
  selectedQuiz: null,
  selectedQuizStatus: 'uninitialized',
  selectedQuizQuestions: [],
  selectedQuizQuestionsStatus: 'uninitialized',
  accreditations: [],
  accreditationsStatus: 'uninitialized',
  instructors: [],
  instructorsStatus: 'uninitialized',
  series: [],
  seriesStatus: 'uninitialized',
  selectedSeries: null,
  selectedSeriesStatus: 'uninitialized',
}

// Extract keys that hold Status values
type StatusKeys<T> = {
  [K in keyof T]: T[K] extends Status ? K : never
}[keyof T]

// Extract keys that don't hold Status values
type DataKeys<T> = Exclude<keyof T, StatusKeys<T>>

// Helper to reduce boilerplate for async thunk reducers
let addAsyncReducers = <
  D extends DataKeys<CreateV5State>,
  S extends StatusKeys<CreateV5State>,
>(
  builder: ActionReducerMapBuilder<CreateV5State>,
  thunk: any,
  dataKey: D,
  statusKey: S,
) => {
  builder
    .addCase(thunk.pending, (state) => {
      state[statusKey] = 'pending' as Status
    })
    .addCase(thunk.rejected, (state, action) => {
      state[statusKey] = 'rejected' as Status
      // eslint-disable-next-line no-console
      console.error(`Failed to fetch ${String(dataKey)}:`, action.error)
    })
    .addCase(thunk.fulfilled, (state, action) => {
      state[statusKey] = 'fulfilled' as Status
      state[dataKey] = action.payload as CreateV5State[D]
    })
}

export let createV5Slice = _createSlice({
  name: 'createV5',
  initialState,
  reducers: {
    selectedLinkedServiceChanged: (state, action: PayloadAction<string>) => {
      state.selectedLinkedService = action.payload
    },
    selectedQuizReset: (state) => {
      state.selectedQuiz = null
      state.selectedQuizStatus = 'uninitialized'
      state.selectedQuizQuestions = []
      state.selectedQuizQuestionsStatus = 'uninitialized'
    },
    selectedSeriesReset: (state) => {
      state.selectedSeries = null
      state.selectedSeriesStatus = 'uninitialized'
    },
  },
  extraReducers: (builder) => {
    addAsyncReducers(builder, fetchCategories, 'categories', 'categoriesStatus')
    addAsyncReducers(builder, fetchCourses, 'courses', 'coursesStatus')
    addAsyncReducers(
      builder,
      fetchLinkedServices,
      'linkedServices',
      'linkedServicesStatus',
    )
    addAsyncReducers(builder, fetchReferences, 'references', 'referencesStatus')
    builder
      .addCase(fetchSelectedCourse.pending, (state) => {
        state.selectedCourseStatus = 'pending'
      })
      .addCase(fetchSelectedCourse.rejected, (state, action) => {
        state.selectedCourseStatus = 'rejected'
        // eslint-disable-next-line no-console
        console.error(`Failed to fetch selected course:`, action.error)
      })
      .addCase(fetchSelectedCourse.fulfilled, (state, action) => {
        state.selectedCourseStatus = 'fulfilled'
        state.courseSteps = action.payload.courseSteps
        state.selectedCourse = action.payload.courseDetails
        state.selectedCourseLegacyCourseSteps = action.payload.legacyCourseSteps
        state.selectedCourseLegacyTextContent = action.payload.legacyTextContent
        state.selectedCourseLegacyTitleScreens =
          action.payload.legacyTitleScreens
      })
    addAsyncReducers(builder, fetchTemplates, 'templates', 'templatesStatus')
    addAsyncReducers(
      builder,
      fetchVideoLibrary,
      'videoLibrary',
      'videoLibraryStatus',
    )
    addAsyncReducers(
      builder,
      fetchShowNotesLibrary,
      'showNotesLibrary',
      'showNotesLibraryStatus',
    )
    addAsyncReducers(
      builder,
      fetchCourseStepsByCourseID,
      'courseSteps',
      'courseStepsStatus',
    )
    addAsyncReducers(
      builder,
      fetchQuizLibrary,
      'quizLibrary',
      'quizLibraryStatus',
    )
    addAsyncReducers(
      builder,
      fetchSelectedQuiz,
      'selectedQuiz',
      'selectedQuizStatus',
    )
    addAsyncReducers(
      builder,
      fetchQuestionsByQuizID,
      'selectedQuizQuestions',
      'selectedQuizQuestionsStatus',
    )
    addAsyncReducers(
      builder,
      fetchAllAccreditations,
      'accreditations',
      'accreditationsStatus',
    )
    addAsyncReducers(
      builder,
      fetchAllInstructors,
      'instructors',
      'instructorsStatus',
    )
    addAsyncReducers(builder, fetchSeries, 'series', 'seriesStatus')
    addAsyncReducers(
      builder,
      fetchSelectedSeries,
      'selectedSeries',
      'selectedSeriesStatus',
    )
  },
})

export let {
  selectedLinkedServiceChanged,
  selectedQuizReset,
  selectedSeriesReset,
} = createV5Slice.actions

export let selectCategories = (state: AppRootState) => state.createV5.categories

export let selectCategoriesByLinkedService =
  (linkedServiceID: string) => (state: AppRootState) =>
    state.createV5.categories[linkedServiceID] || []

export let selectCourses = (state: AppRootState) => state.createV5.courses

export let selectCoursesStatus = (state: AppRootState) =>
  state.createV5.coursesStatus

export let selectCategoriesAll = createSelector(
  [selectCategories],
  (categories): Section[] =>
    Object.values(categories)
      .flat()
      .toSorted((a, b) => a.name.localeCompare(b.name)),
)

export let selectCategoriesStatus = (state: AppRootState) =>
  state.createV5.categoriesStatus

export let selectLinkedServices = (state: AppRootState) =>
  state.createV5.linkedServices

export let selectLinkedServicesStatus = (state: AppRootState) =>
  state.createV5.linkedServicesStatus

export let selectReferences = (state: AppRootState) => state.createV5.references

export let selectReferencesByLinkedService =
  (linkedServiceID: string) => (state: AppRootState) =>
    state.createV5.references[linkedServiceID] || []

export let selectReferencesStatus = (state: AppRootState) =>
  state.createV5.referencesStatus

export let selectSelectedCourse = (state: AppRootState) =>
  state.createV5.selectedCourse

export let selectSelectedCourseStatus = (state: AppRootState) =>
  state.createV5.selectedCourseStatus

export let selectSelectedLinkedService = (state: AppRootState) =>
  state.createV5.selectedLinkedService

export let selectTemplates = (state: AppRootState) => state.createV5.templates

export let selectTemplatesByLinkedService =
  (linkedServiceID: string) => (state: AppRootState) =>
    state.createV5.templates[linkedServiceID] || []

export let selectTemplatesStatus = (state: AppRootState) =>
  state.createV5.templatesStatus

export let selectVideoLibrary = (state: AppRootState) =>
  state.createV5.videoLibrary

export let selectVideoLibraryStatus = (state: AppRootState) =>
  state.createV5.videoLibraryStatus

export let selectShowNotesLibrary = (state: AppRootState) =>
  state.createV5.showNotesLibrary

export let selectShowNotesLibraryStatus = (state: AppRootState) =>
  state.createV5.showNotesLibraryStatus

export let selectQuizLibrary = (state: AppRootState) =>
  state.createV5.quizLibrary

export let selectQuizLibraryStatus = (state: AppRootState) =>
  state.createV5.quizLibraryStatus

export let selectSelectedQuiz = (state: AppRootState) =>
  state.createV5.selectedQuiz

export let selectSelectedQuizStatus = (state: AppRootState) =>
  state.createV5.selectedQuizStatus

export let selectSelectedQuizQuestions = (state: AppRootState) =>
  state.createV5.selectedQuizQuestions

export let selectSelectedQuizQuestionsStatus = (state: AppRootState) =>
  state.createV5.selectedQuizQuestionsStatus

export let selectCourseSteps = (state: AppRootState) =>
  state.createV5.courseSteps

export let selectCourseStepsStatus = (state: AppRootState) =>
  state.createV5.courseStepsStatus

export let selectCourseFormLoadingStatuses = createSelector(
  [
    selectCategoriesStatus,
    selectCoursesStatus,
    selectLinkedServicesStatus,
    selectReferencesStatus,
    selectSelectedCourseStatus,
    selectTemplatesStatus,
  ],
  (
    categories,
    courses,
    linkedServices,
    references,
    selectedCourse,
    templates,
  ) => ({
    categories,
    courses,
    linkedServices,
    references,
    selectedCourse,
    templates,
  }),
)

export let selectLegacyContentData = (state: AppRootState) => ({
  textContent: state.createV5.selectedCourseLegacyTextContent,
  titleScreens: state.createV5.selectedCourseLegacyTitleScreens,
  steps: state.createV5.selectedCourseLegacyCourseSteps,
})

export let selectAccreditations = (state: AppRootState) =>
  state.createV5.accreditations

export let selectAccreditationsStatus = (state: AppRootState) =>
  state.createV5.accreditationsStatus

export let selectAccreditationsByCourseID = (courseID: string) =>
  createSelector([selectAccreditations], (accreditations) =>
    accreditations.filter((a) => a.courseID === courseID),
  )

export let selectInstructors = (state: AppRootState) =>
  state.createV5.instructors

export let selectInstructorsStatus = (state: AppRootState) =>
  state.createV5.instructorsStatus

export let selectInstructorsByCourseID = (courseID: string) =>
  createSelector([selectInstructors], (instructors) =>
    instructors
      .filter((i) => i.courseAssociations.some((a) => a.courseID === courseID))
      .toSorted((a, b) => {
        let aSeq =
          a.courseAssociations.find((ca) => ca.courseID === courseID)
            ?.sequence ?? 0
        let bSeq =
          b.courseAssociations.find((ca) => ca.courseID === courseID)
            ?.sequence ?? 0
        return aSeq - bSeq
      }),
  )

export let selectSeries = (state: AppRootState) => state.createV5.series
export let selectSeriesStatus = (state: AppRootState) =>
  state.createV5.seriesStatus

export let selectSelectedSeries = (state: AppRootState) =>
  state.createV5.selectedSeries
export let selectSelectedSeriesStatus = (state: AppRootState) =>
  state.createV5.selectedSeriesStatus

export default createV5Slice.reducer
